import React from 'react';
import { graphql } from 'gatsby';
import PropTypes from 'prop-types';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import FavoriteButton from '../../atoms/FavoriteButton';
import { useLanguageLink } from '../../hooks/useLanguageLink';
import { useTranslation } from '../../hooks/useTranslatedField';
import { DNGatsbyTemplate } from '../DriveNinjaTemplate/gatsby';
import Grid from '@mui/material/Unstable_Grid2';
import Link from '@mui/material/Link';
import camelCase from 'lodash/camelCase';
import { createIntl, FormattedMessage, FormattedNumberParts, IntlProvider } from 'react-intl';
import ar from './ar.json';
import en from './en.json';
import { LocaleProvider } from '../../../contexts/language';
import CarImageGallery from '../../molecules/CarImageGallery';
import { ComparisonContext } from '../../../contexts/comparison';
import ShareItemButton from '../../atoms/ShareItemButton';
import CarTitle from '../../atoms/CarTitle';
import Button from '@mui/material/Button';
import { GatsbyImage, getImage, StaticImage } from 'gatsby-plugin-image';
import CarPriceBox from '../../atoms/CarPriceBox';
import FeatureContainer from '../../molecules/FeatureContainer';
import { FeatureGroup } from '../../molecules/FeatureGroup';
import fieldConfig from '../../../config/fieldConfig';
import OfferCardFavorite from './offerCardFavorite';
import { useCarLink } from '../../hooks/useCarLink';
import { useSearchString } from '../../hooks/useSearchString';
import { filterSearchResults } from '../../../functions/filterSearchResults';
import allFields from '../../../config/allFields.json';
import { QueryParamProvider } from 'use-query-params';
import { ReachAdapter } from 'use-query-params/adapters/reach';
import { hexToRgbA } from '../../../functions/hexToRgbA';
import { selectTextColor } from '../../../functions/selectTextColor';
import GetHelpPanel from '../../molecules/PureGetHelpPanel/GetHelpPanel';

const languages = {
  ar, en
};

const TrimFavoriteIcon = ({ slug }) => {
  const { addTrimLevel, removeTrimLevel, compareConfig } = React.useContext(ComparisonContext);
  return <FavoriteButton
    outlined
    onToggleFavorite={() => compareConfig?.trimLevels?.indexOf(slug) === -1 ? addTrimLevel(slug) : removeTrimLevel(slug)}
    favorite={compareConfig?.trimLevels?.indexOf(slug) !== -1}
  />;
};
TrimFavoriteIcon.propTypes = {
  slug: PropTypes.string.isRequired
};


const TrimLevelPage = ({ pageContext: { lang, direction }, data }) => {
  const intl = createIntl({ locale: lang || 'en', messages: languages[lang] || languages.en });
  const l = useLanguageLink();
  const t = useTranslation();
  const cl = useCarLink();
  const { searchParameters, url: searchURL, searchParamString } = useSearchString();
  const [viewAllResults, setViewAllResults] = React.useState(false);
  const hasSearchResults = searchURL?.length;

  const allResults = data.allDriveNinjaModelType.edges.map(({ node }) => node).map(({ trimLevels, ...mdl }) => ({
    ...mdl,
    trimLevels: trimLevels.edges.map(({ node }) => node).map(({ offers, lifestyles, carTypes, carShapes, ...tl }) => ({
      ...tl,
      offers: offers.edges.map(({ node }) => node).map(o => ({
        ...o, exteriorColor: o?.exteriorColor?.genericColor?.slug, interiorColor: o?.interiorColor?.genericColor?.slug
      })),
      lifestyles: lifestyles.edges.map(({ node }) => node.slug),
      carTypes: carTypes.edges.map(({ node }) => node.slug),
      carShapes: carShapes.edges.map(({ node }) => node.slug)
    }))
  }));

  const searchResultData = hasSearchResults && !viewAllResults ? filterSearchResults({
    fieldLookups: {}, searchParameters, searchResults: allResults, allFields
  }) : allResults;
  const allTrimLevels = searchResultData.map(({ trimLevels }) => trimLevels).reduce((a, b) => [...a, ...b], []);
  const allOffers = allTrimLevels.map(tr => tr.offers.map(o => ({
    ...o, trimLevel: { slug: tr.slug, modelYear: tr.modelYear }
  }))).reduce((a, b) => [...a, ...b], []).sort((a, b) => a?.monthlyAmount - b?.monthlyAmount);


  const useGatsbySortedImages = ({ images, name, colorCode, colorName }) => {
    const elements = images?.sort((a, b) => a?.orderingPriority - b?.orderingPriority).map(({ localImage }, idx) =>
      <GatsbyImage
        key={idx}
        image={getImage(localImage)}
        layout="fixed"
        alt={t(name) || ''}
        height={200}
        style={{ width: '100%' }}
      />);
    if (!elements?.length) {
      return [<StaticImage
        key={1}
        src={'./images/trimLevelPlaceholder.png'}
        layout="fixed"
        alt={`${name || ''}`}
        height={200}
        style={{ width: '100%' }}
      />];
    }
    if (colorName) {
      return elements.map((e, id) => (<Box
        key={id}
        sx={{ position: 'relative' }}
      >
        {e}
        <Box
          style={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            left: '66%',

            backgroundColor: colorCode ? hexToRgbA(colorCode, 0.75) : hexToRgbA('#fff', 0.75),
            color: colorCode ? selectTextColor(colorCode, '#fff', '#3a3a3c') : '#3a3a3c'
          }}
        >
          <Stack sx={{ justifyContent: 'center', height: '100%' }}>
            <Stack direction={'row'} sx={{ justifyContent: 'center' }}>
              <Box sx={{ p: 2 }}><Typography
                sx={{ textWrap: 'wrap', fontSize: '1rem', fontWeight: '700' }}>{colorName}</Typography></Box>
            </Stack>
          </Stack>
        </Box>
      </Box>));
    }
    return elements;
  };

  const manufacturer = data.driveNinjaManufacturerType;
  const model = data.driveNinjaModelType;
  const trimLevel = data.driveNinjaTrimLevelType;

  const allOfferIDS = allOffers.map(({ uuid }) => uuid);
  const trimLevelOffers = data.allDriveNinjaPurchaseNewOfferType.edges.map(({ node }) => node).filter(({ uuid }) => allOfferIDS.indexOf(uuid) !== -1).sort((a, b) => a?.monthlyAmount - b?.monthlyAmount);
  const bestNew = trimLevelOffers?.[0];
  // Filter out offers which aren't in allOffers


  return (<LocaleProvider localeConfig={{ lang, direction }}>
    <DNGatsbyTemplate
      breadcrumbs={hasSearchResults ? [{
        name: intl.formatMessage({
          id: 'breaddcrumbs.SearchPage', defaultMessage: 'Search'
        }), path: searchURL
      }, {
        name: t(manufacturer.translatedName), path: cl({ mfrSlug: manufacturer.slug })
      }, {
        name: t(model.translatedName), path: cl({ mfrSlug: manufacturer.slug, mdlSlug: model?.slug })
      }, {
        name: t(trimLevel.translatedName), path: l(`/trim-level/${trimLevel.slug}`)
      }] : [{
        name: intl.formatMessage({
          id: 'browseMake.BrowseMakeBreadcrumbs', defaultMessage: 'Browse by Manufacturer'
        }), path: l('/make/')
      }, {
        name: t(manufacturer.translatedName), path: cl({ mfrSlug: manufacturer.slug })
      }, {
        name: t(model.translatedName), path: cl({ mfrSlug: manufacturer.slug, mdlSlug: model?.slug })
      }, {
        name: t(trimLevel.translatedName), path: l(`/trim-level/${trimLevel.slug}`)
      }]}
    >
      <IntlProvider locale={lang} messages={languages[lang] || languages.en}>
        <Stack spacing={2} sx={{ mb: 3 }}>
          <Grid container spacing={3}>
            <Grid xs={12} md={7} lg={8}>
              <CarImageGallery images={trimLevel.images?.sort((a, b) => a?.orderingPriority - b?.orderingPriority)} />
            </Grid>
            <Grid xs={12} md={5} lg={4}>
              <Stack spacing={3}>
                <Box sx={{ textAlign: 'center' }}>
                  <CarTitle>{t(manufacturer.translatedName)} {t(trimLevel.translatedSingularName)}</CarTitle>
                </Box>
                <Stack spacing={1} direction={'row'} sx={{ width: '100%' }}>
                  {bestNew ? (<>
                    <CarPriceBox sx={{ textDecoration: 'none' }} component={Link} href={'#newOffers'}
                      title={'View new offers'}>
                      <Stack sx={{ fontWeight: 600 }}>
                        <Box sx={{ fontWeight: '500', fontSize: '0.7rem', color: 'offerPricingCard.startsFrom' }}>
                          <FormattedMessage id={'NewOfferPage.financeNewFrom__amount'}
                            defaultMessage={'Monthly From'} />
                        </Box>
                        <Box>
                          <Typography sx={{ fontWeight: '700', fontSize: '1.2rem' }} color={'primary'}>
                            <FormattedNumberParts
                              minimumFractionDigits={0}
                              maximumFractionDigits={0}
                              style={'currency'}
                              currency={bestNew.currency}
                              value={bestNew.monthlyAmount}
                            >{(parts) => parts.map(({ value, type }, i) => type === 'currency' ?
                                <Box key={i} component="span" sx={{
                                  color: 'offerPricingCard.currency', fontSize: '1rem'
                                }}>{value}</Box> : value)}
                            < /FormattedNumberParts>
                          </Typography>
                        </Box>
                      </Stack>
                    </CarPriceBox>
                    <CarPriceBox sx={{ textDecoration: 'none' }} component={Link} href={'#newOffers'}
                      title={'View new offers'}>
                      <Stack sx={{ justifyContent: 'space-between' }}>
                        <Box sx={{ fontWeight: '500', fontSize: '0.7rem', color: 'offerPricingCard.startsFrom' }}>
                          <FormattedMessage id={'NewOfferPage.newFrom__amount'} defaultMessage={'Buy New from'} />
                        </Box>
                        <Box>
                          <Typography sx={{ fontWeight: '700', fontSize: '1.2rem' }} color={'primary'}>
                            <FormattedNumberParts
                              minimumFractionDigits={0}
                              maximumFractionDigits={0}
                              style={'currency'}
                              currency={bestNew.currency}
                              value={bestNew.purchaseAmount}
                            >{(parts) => parts.map(({ value, type }, i) => type === 'currency' ?
                                <Box key={i} component="span" sx={{
                                  color: 'offerPricingCard.currency', fontSize: '1rem'
                                }}>{value}</Box> : value)}
                            < /FormattedNumberParts>
                          </Typography>
                        </Box>
                      </Stack>
                    </CarPriceBox>
                  </>) : null}
                </Stack>
                <Stack direction={'row'} spacing={1}>
                  <TrimFavoriteIcon slug={trimLevel?.slug} />
                  <ShareItemButton
                    model={t(model.translatedName)}
                    make={t(manufacturer.translatedName)}
                    url={typeof window !== 'undefined' ? window.location.href : ''}
                  />
                </Stack>
              </Stack>
            </Grid>
            <Grid xs={12}>

              <Stack spacing={2} sx={{ mb: 3 }}>
                {trimLevel.translatedDescription.en?.length > 10 ? (<Stack spacing={2}>
                  {t(trimLevel.translatedDescription).split('\n').map((para, k) => <Typography variant={'body1'}
                    key={k}>{para}</Typography>)}
                </Stack>) : null}

                <FeatureContainer>
                  {fieldConfig.map((fg, k) => (<FeatureGroup
                    jargon={Object.fromEntries(data?.allDriveNinjaJargonEntryType?.edges.map(({ node }) => node).map(({
                      featureSlug,
                      ...rest
                    }) => [camelCase(featureSlug), { featureSlug, ...rest }]))}
                    key={k} featureGroup={fg} trimLevel={trimLevel} />))}
                </FeatureContainer>
                <a id={'newOffers'} />
                {trimLevelOffers?.length > 0 && (<>
                  <Typography variant={'h3'}>Offers for New Vehicles</Typography>
                  {!viewAllResults && hasSearchResults ? (<>
                    <Typography variant={'body1'}>
                      <FormattedMessage
                        id={'trimLevelPage.offerList.searchResultsExplainer'}
                        defaultMessage={'Below are the {model} offers that match your search, from a total of {length} currently available. Tap on any offer below to view more details, compare, or start building your deal now!'}
                        values={{ length: allOffers.length, model: t(trimLevel.translatedSingularName) }}
                      />
                    </Typography>
                    <Box sx={{
                      justifyContent: 'center', display: 'flex'
                    }}>
                      <Button
                        color={'primary'}

                        variant="contained"
                        onClick={() => setViewAllResults(true)}
                      >
                        <FormattedMessage id={'trimLevelPage.offerList.showAllModelsButton'}
                          defaultMessage={'Show All Offers'} />
                      </Button>
                    </Box>
                  </>) : (<>

                    <Typography variant={'body1'}>
                      <FormattedMessage
                        id={'trimLevelPage.offerList.allModelsExplainer'}
                        defaultMessage={'Below are the {model} offers currently available. Tap on any offer below to view more details, compare, or start building your deal now!'}
                        values={{ model: t(trimLevel.translatedSingularName) }}
                      />
                    </Typography>

                    {hasSearchResults && (<Box sx={{
                      justifyContent: 'center', display: 'flex'
                    }}>
                      <Button
                        color={'primary'}
                        variant="contained"
                        onClick={() => setViewAllResults(false)}
                      >
                        <FormattedMessage id={'modelPage.showMatchingModelsButton'}
                          defaultMessage={'Show Matching Offers'} />
                      </Button>
                    </Box>)}
                  </>)}
                </>)}
                {trimLevelOffers?.length ? (<>
                  <Grid container spacing={2} sx={{ mt: 2, mb: 2 }}>

                    {trimLevelOffers?.map(({
                      uuid,
                      stockAvailability,
                      monthlyAmount,
                      purchaseAmount,
                      currency,
                      images,
                      provider,
                      interiorColor,
                      exteriorColor
                    }) => {
                      const trimLevelImages = trimLevel?.offerImages?.filter(({
                        exteriorColor
                      }) => exteriorColor?.slug).filter(({
                        exteriorColor: ec
                      }) => ec?.slug === exteriorColor?.slug);
                      const matchingTrimLevelImages = trimLevelImages?.length ? trimLevelImages : null;
                      const greyImages = trimLevel?.grayOfferImages.length ? trimLevel?.grayOfferImages : null;

                      return (<Grid key={uuid} xs={12} md={6} lg={4}>
                        <a id={uuid} />
                        <OfferCardFavorite
                          uuid={uuid}
                          images={useGatsbySortedImages({
                            images: images?.length && images || matchingTrimLevelImages || greyImages || [],
                            translatedSingularName: trimLevel?.translatedSingularName,
                            colorName: (images?.length || matchingTrimLevelImages) ? undefined : t(exteriorColor?.translatedName),
                            colorCode: (images?.length || matchingTrimLevelImages) ? undefined : exteriorColor?.genericColor?.htmlColorCode
                          })}
                          cardProps={{ sx: { width: '100%' } }}
                          slug={trimLevel?.slug}
                          title={`${t(trimLevel.translatedSingularName)}`}
                          officialDealer={t(manufacturer.translatedName)}
                          finalPrice
                          link={cl({
                            mfrSlug: manufacturer.slug,
                            mdlSlug: model?.slug,
                            trimSlug: trimLevel?.slug,
                            newOfferUUID: uuid,
                            search: searchParamString
                          })}
                          //search: searchParamString
                          linkText={intl.formatMessage({
                            id: 'modelPage.linkTitle', defaultMessage: 'View offer for {make} {model}'
                          }, {
                            make: t(manufacturer.translatedName), model: t(trimLevel.translatedSingularName)
                          })}
                          stockAvailability={stockAvailability}
                          monthlyPrice={monthlyAmount ? parseFloat(monthlyAmount) : undefined}
                          purchasePrice={purchaseAmount ? parseFloat(purchaseAmount) : undefined}
                          currency={currency}
                          term={currency ? 'Monthly' : undefined}
                          providerName={t(provider?.translatedName)}
                          providerLogo={provider?.logoFile ? (<GatsbyImage
                            image={getImage(provider?.logoFile)}
                            layout="fixed"
                            alt={t(provider?.translatedName) || ''}
                            height={50}
                          />) : undefined}
                          interiorColor={interiorColor?.translatedName && t(interiorColor?.translatedName)}
                          exteriorColor={exteriorColor?.translatedName && t(exteriorColor?.translatedName)}
                          interiorColorCode={interiorColor?.htmlColorCode || interiorColor?.genericColor?.htmlColorCode}
                          exteriorColorCode={exteriorColor?.htmlColorCode || exteriorColor?.genericColor?.htmlColorCode}
                        />
                      </Grid>);

                    })}
                  </Grid>
                </>) : null}
                <GetHelpPanel
                  offer={bestNew}
                  offerId={bestNew?.id}
                  modelId={model?.id}
                  trimLevelId={trimLevel?.id}
                  providerId={bestNew?.provider?.id}
                />
              </Stack>

            </Grid>

          </Grid>
        </Stack>
      </IntlProvider>
    </DNGatsbyTemplate>
  </LocaleProvider>);
};


TrimLevelPage.propTypes = {
  pageContext: PropTypes.shape({
    lang: PropTypes.string.isRequired, direction: PropTypes.string.isRequired
  }), data: PropTypes.shape({
    allDriveNinjaModelType:PropTypes.shape({
      edges:PropTypes.arrayOf(PropTypes.shape({ node:PropTypes.shape({

      })
      })      )
    }),
    driveNinjaManufacturerType: PropTypes.shape({
      translatedName: PropTypes.shape({
        ar: PropTypes.string, en: PropTypes.string.isRequired
      }).isRequired, slug: PropTypes.string.isRequired
    }),
    allDriveNinjaJargonEntryType: PropTypes.shape({
      edges:PropTypes.arrayOf(PropTypes.shape({

      }))
    }),
    allDriveNinjaPurchaseNewOfferType: PropTypes.shape({
      edges: PropTypes.arrayOf(PropTypes.shape({
        node: PropTypes.shape({
          images: PropTypes.arrayOf(PropTypes.shape({})),
          purchaseAmount: PropTypes.string.isRequired,
          currency: PropTypes.string.isRequired,
          monthlyAmount: PropTypes.string.isRequired,
          stockAvailability: PropTypes.string.isRequired,
          stockAvailabilityRank: PropTypes.number.isRequired
        })
      }))
    }), driveNinjaTrimLevelType: PropTypes.shape({
      translatedSingularName: PropTypes.shape({
        ar: PropTypes.string, en: PropTypes.string.isRequired
      }).isRequired,
      translatedName: PropTypes.shape({
        ar: PropTypes.string, en: PropTypes.string.isRequired
      }).isRequired,
      translatedDescription: PropTypes.shape({
        ar: PropTypes.string, en: PropTypes.string.isRequired
      }).isRequired,
      slug: PropTypes.string.isRequired,
      offerImages: PropTypes.arrayOf(PropTypes.shape({})),
      grayOfferImages: PropTypes.arrayOf(PropTypes.shape({})),
      images: PropTypes.arrayOf(PropTypes.shape({
        orderingPriority: PropTypes.number.isRequired,
        thumbnail: PropTypes.shape({}),
        regularImage: PropTypes.shape({}),
        fullImage: PropTypes.shape({})
      })).isRequired,
      orderImages: PropTypes.arrayOf(PropTypes.shape({}))
    }), driveNinjaModelType: PropTypes.shape({
      translatedName: PropTypes.shape({
        ar: PropTypes.string, en: PropTypes.string.isRequired
      }).isRequired, slug: PropTypes.string.isRequired
    })
  })
};


export const query = graphql`
    fragment tlCompare on DriveNinjaTrimLevelType{
        adaptiveCruiseControl
        androidAuto
        appleCarPlay
        automaticAirConditioning
        automaticFoldingWingMirrors
        automaticHeadlights
        auxInput
        blindSpotWarning
        bluetooth
        bootCapacity
        camera360
        chargeTime
        cooledSeats
        crashWarningSystem
        cruiseControl
        daytimeRunningLights
        dealershipCode
        dimensionHeight
        dimensionLength
        dimensionWidth
        carTypes {
            edges {
                node {
                    translatedName {
                        en
                        ar
                    }
                    slug
                }
            }
        }
        carShapes {
            edges {
                node {
                    translatedName {
                        en
                        ar
                    }
                    slug
                }
            }
        }
        drivingWheels {
            translatedName {
                en
                ar
            }
            slug
        }
        engineCapacity
        electronicStabilityControl
        electronicParkingBrake
        electricWingMirrors
        electricPassengerSeat
        electricDriverSeat
        frontFogLights
        frontParkingSensors
        fuelConsumption
        fuelTankCapacity
        gearbox {
            slug
            translatedName {
                en
                ar
            }
        }
        fuelType {
            slug
            translatedName {
                en
                ar
            }
        }
        hillStartAssist
        hillDescentAssist
        headlampsLed
        headlampsLaser
        headUpDisplay
        handsFreeTailgate
        isofixChildSeat
        keylessEntry
        laneDepartureAssist
        leatherSeats
        leatherWrappedSteeringWheel
        lifestyles {
            edges {
                node {
                    searchRank
                    translatedName {
                        en
                        ar
                    }
                }
            }
        }
        modelYear
        navigation
        numAirbags
        numCylinders
        numDoors
        numGears
        numSeats
        numSpeakers
        paddleShifter
        panoramicSunroof
        power
        powerToWeight
        pushButtonStart
        range
        rearFogLights
        rearParkingSensors
        rearPassengerAcVents
        rearWindscreenShade
        remoteVehicleStart
        reversingCamera
        roofRackRails
        safetyRating
        searchRank
        soundSystemBrand {
            translatedName {
                ar
                en
            }
            slug
        }
        splitFoldingRearSeats
        sportsSeats
        sunroofFront
        time0100
        topSpeed
        torque
        touchscreen
        tractionControlSystem
        turningCircle
        tyrePressureMonitoringSystem
        usbInput
        voiceControl
        weight
        wheelSize
        wheelbase
        wifiConnection
        wirelessCharging
    }
    query ($slug: String!, $mdlSlug: String!, $mfrSlug: String!) {
        driveNinjaManufacturerType(slug: {eq: $mfrSlug}) {
            translatedName {
                en
                ar
            }
            slug
            id
        }
        allDriveNinjaModelType(
            filter: {trimLevels: {edges: {elemMatch: {node: {slug: {eq: $slug}}}}}}
        ) {
            edges {
                node {
                    manufacturer {
                        slug
                        translatedName {
                            ar
                            en
                        }
                    }
                    slug
                    id: remoteId
                    translatedName {
                        ar
                        en
                    }
                    trimLevels {
                        edges {
                            node {
                                id: remoteId
                                slug
                                translatedSingularName {
                                    ar
                                    en
                                }
                                carTypes {
                                    edges {
                                        node {
                                            slug
                                        }
                                    }
                                }
                                carShapes {
                                    edges {
                                        node {
                                            slug
                                        }
                                    }
                                }
                                drivingWheels {
                                    slug
                                }
                                lifestyles {
                                    edges {
                                        node {
                                            slug
                                        }
                                    }
                                }
                                gearbox {
                                    slug
                                }
                                fuelType {
                                    slug
                                }
                                soundSystemBrand {
                                    slug
                                }
                                offers: newOffers {
                                    edges {
                                        node {
                                            provider{
                                                id: remoteId    
                                            }
                                            id: remoteId
                                            uuid
                                            currency
                                            purchaseAmount
                                            monthlyAmount
                                            exteriorColor {
                                                slug
                                                genericColor{
                                                    slug
                                                }
                                            }
                                            interiorColor {
                                                slug
                                                genericColor{
                                                    slug
                                                }
                                            }
                                            stockAvailability
                                            stockAvailabilityRank
                                        }
                                    }
                                }
                                adaptiveCruiseControl
                                androidAuto
                                appleCarPlay
                                automaticAirConditioning
                                automaticFoldingWingMirrors
                                automaticHeadlights
                                auxInput
                                blindSpotWarning
                                bluetooth
                                bootCapacity
                                camera360
                                chargeTime
                                cooledSeats
                                crashWarningSystem
                                cruiseControl
                                daytimeRunningLights
                                dealershipCode
                                dimensionHeight
                                dimensionLength
                                dimensionWidth
                                engineCapacity
                                electronicStabilityControl
                                electronicParkingBrake
                                electricWingMirrors
                                electricPassengerSeat
                                electricDriverSeat
                                frontFogLights
                                frontParkingSensors
                                fuelConsumption
                                fuelTankCapacity
                                hillStartAssist
                                hillDescentAssist
                                headlampsLed
                                headlampsLaser
                                headUpDisplay
                                handsFreeTailgate
                                isofixChildSeat
                                keylessEntry
                                laneDepartureAssist
                                leatherSeats
                                leatherWrappedSteeringWheel
                                modelYear
                                navigation
                                numAirbags
                                numCylinders
                                numDoors
                                numGears
                                numSeats
                                numSpeakers
                                paddleShifter
                                panoramicSunroof
                                power
                                powerToWeight
                                pushButtonStart
                                range
                                rearFogLights
                                rearParkingSensors
                                rearPassengerAcVents
                                rearWindscreenShade
                                remoteVehicleStart
                                reversingCamera
                                roofRackRails
                                safetyRating
                                searchRank
                                splitFoldingRearSeats
                                sportsSeats
                                sunroofFront
                                time0100
                                topSpeed
                                torque
                                touchscreen
                                tractionControlSystem
                                turningCircle
                                tyrePressureMonitoringSystem
                                updatedAt
                                usbInput
                                uuid
                                voiceControl
                                weight
                                wheelSize
                                wheelbase
                                wifiConnection
                                wirelessCharging
                            }
                        }
                    }
                }
            }
        }
        driveNinjaTrimLevelType(slug: {eq: $slug}) {
            slug
            id: remoteId
            translatedDescription {
                en
                ar
            }
            translatedExcerpt {
                en
                ar
            }
            translatedName {
                en
                ar
            }
            offerImages: images {
                orderingPriority
                interiorColor{
                    slug
                    genericColor{
                        slug
                    }
                }
                exteriorColor{
                    slug
                    genericColor{
                        slug
                    }
                }
                localImage {
                    publicURL
                    childImageSharp {
                        gatsbyImageData(height: 200, layout: FIXED)
                        id
                    }
                }
            }
            grayOfferImages: images {
                orderingPriority
                localImage {
                    publicURL
                    childImageSharp {
                        gatsbyImageData(height: 200, layout: FIXED, transformOptions:{grayscale:true})
                        id
                    }
                }
            }
            translatedSingularName {
                ar
                en
            }
            images {
                orderingPriority
                thumbnail: localImage {
                    childImageSharp {
                        gatsbyImageData(height: 100, width:100, layout: FIXED, transformOptions:{
                            fit:COVER,
                            cropFocus:CENTER
                        })
                    }
                }
                regularImage: localImage {
                    childImageSharp {
                        gatsbyImageData(width: 600, layout: FIXED)
                    }
                }
                fullImage: localImage {
                    childImageSharp {
                        gatsbyImageData(layout: FULL_WIDTH)
                    }
                }
            }




            ...tlCompare
        }

        driveNinjaModelType(slug: {eq: $mdlSlug}) {
            translatedName {
                en
                ar
            }
            id: remoteId
            slug
        }
        allDriveNinjaPurchaseNewOfferType(
            filter: {available: {eq: true}, trimLevel: {slug: {eq: $slug}}}
            sort: {purchaseAmount: ASC}
        ) {
            edges {
                node {
               
                    id: remoteId
                    trimLevel {
                        slug
                    }
                    exteriorColor {
                        slug
                        id
                        translatedName {
                            en
                            ar
                        }
                        htmlColorCode
                        genericColor {
                            id
                            htmlColorCode
                            slug
                        }
                    }
                    interiorColor{
                        id
                        slug
                        translatedName {
                            en
                            ar
                        }
                        htmlColorCode
                        genericColor {
                            id
                            htmlColorCode
                            slug
                        }
                    }
                    provider {
                        id: remoteId
                        translatedName {
                            ar
                            en
                        }
                        slug
                        logoFile {
                            childImageSharp {
                                gatsbyImageData(height: 50, layout: FIXED)
                                id
                            }
                        }
                    }
                    uuid
                    purchaseAmount
                    currency
                    monthlyAmount
                    stockAvailability
                    stockAvailabilityRank
                    images {
                        orderingPriority
                        localImage {

                            publicURL
                            childImageSharp {
                                gatsbyImageData(height: 200, layout: FIXED)
                                id
                            }
                        }
                    }
                }
            }
        }
        allDriveNinjaJargonEntryType{
            edges{
                node{
                    slug
                    featureSlug
                    pageTitle
                    excerpt
                }
            }
        }
    }

`;


const PageWrapper = (props) => {
  return (<QueryParamProvider adapter={ReachAdapter}
  >
    <TrimLevelPage {...props} />
  </QueryParamProvider>);
};
export default PageWrapper;
